import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import usePastTime from 'hooks/usePastTime';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import utils, { getFileCountInFolder, getIsNodeFile, getNodeSize } from 'utils';
import { FileOrderEntry, SelectedStageEntry } from '../utils/stageFiles';

const joinMeta = (...parts: string[]) => parts.filter(Boolean).join(' · ');

const useStageNodeMeta = () => {
  const { t } = useTranslation();

  const pastTime = usePastTime();

  const getNodeMeta = useCallback(
    (node: DataRoomNode) =>
      getIsNodeFile(node)
        ? joinMeta(
            utils.humanizeFileSize(getNodeSize(node)),
            pastTime(node.createdAt),
          )
        : joinMeta(
            t('folderItemsCount', { count: getFileCountInFolder(node) }),
            utils.humanizeFileSize(getNodeSize(node)),
          ),
    [pastTime, t],
  );

  const getEntryMeta = useCallback(
    ({ node, selectedCount, totalCount, size }: SelectedStageEntry) =>
      getIsNodeFile(node) || selectedCount === totalCount
        ? getNodeMeta(node)
        : joinMeta(
            t('selectedOfTotalFiles', {
              selected: selectedCount,
              count: totalCount,
            }),
            utils.humanizeFileSize(size),
          ),
    [getNodeMeta, t],
  );

  // A file added from a folder shows that folder instead of its date.
  const getOrderEntryMeta = useCallback(
    ({ node, parentName }: FileOrderEntry) =>
      parentName
        ? joinMeta(parentName, utils.humanizeFileSize(getNodeSize(node)))
        : getNodeMeta(node),
    [getNodeMeta],
  );

  return { getNodeMeta, getEntryMeta, getOrderEntryMeta };
};

export default useStageNodeMeta;
